/**
 * juliette-signals — Helpers de la surface des signaux proactifs de Juliette.
 *
 * Source : table juliette_signals, alimentee par le cron quotidien
 * run_juliette_signal_detection() (3 types : dormant, mandate_expiring,
 * hot_blocked). RLS team-scoped : SELECT + UPDATE autorises a l'utilisateur,
 * donc zero Edge Function cote client.
 *
 * Statuts : pending (a traiter) / seen (vu) / resolved (condition disparue,
 * pose par le cron) / dismissed (ecarte par l'agent). La surface n'affiche que
 * les signaux encore actifs (pending) ; "Ecarter" passe en dismissed.
 */

import { supabase } from "@/integrations/supabase/client";

export type SignalSeverity = "urgent" | "warning" | "info";

export interface JulietteSignal {
  id: string;
  contactId: string | null;
  contactName: string | null;
  signalType: string;
  severity: SignalSeverity;
  title: string;
  detail: string | null;
  detectedAt: string;
  /** Données structurées du signal (ex campaign_reco : sequence_id, label…). */
  payload: Record<string, unknown> | null;
}

/** Rang de tri par severite (urgent d'abord). */
const SEVERITY_RANK: Record<string, number> = { urgent: 0, warning: 1, info: 2 };

/** Libelle lisible d'un type de signal. */
export function signalTypeLabel(type: string): string {
  switch (type) {
    case "dormant":
      return "Contact dormant";
    case "mandate_expiring":
      return "Mandat expirant";
    case "hot_blocked":
      return "Lead chaud bloqué";
    case "to_triage":
      return "À trier";
    case "campaign_reco":
      return "Conseil campagne";
    default:
      return "Signal";
  }
}

/**
 * Liste les signaux actifs (pending) de la team, urgents d'abord, avec le nom
 * du contact resolu en une requete (meme pattern que juliette-actions).
 */
export async function listJulietteSignals(limit = 50): Promise<JulietteSignal[]> {
  const { data, error } = await supabase
    .from("juliette_signals")
    .select("id, contact_id, signal_type, severity, title, detail, detected_at, payload")
    .eq("status", "pending")
    .order("detected_at", { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);

  type Row = {
    id: string;
    contact_id: string | null;
    signal_type: string;
    severity: string;
    title: string;
    detail: string | null;
    detected_at: string;
    payload: Record<string, unknown> | null;
  };
  const rows = (data ?? []) as Row[];

  const ids = [...new Set(rows.map((r) => r.contact_id).filter((v): v is string => !!v))];
  const nameById = new Map<string, string>();
  if (ids.length > 0) {
    const { data: contacts } = await supabase
      .from("immo_contacts")
      .select("id, name, first_name, last_name")
      .in("id", ids);
    type C = { id: string; name: string | null; first_name: string | null; last_name: string | null };
    for (const c of (contacts ?? []) as C[]) {
      const label = (c.name ?? "").trim() || [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
      if (label) nameById.set(c.id, label);
    }
  }

  return rows
    .map((r) => ({
      id: r.id,
      contactId: r.contact_id,
      contactName: r.contact_id ? nameById.get(r.contact_id) ?? null : null,
      signalType: r.signal_type,
      severity: (["urgent", "warning", "info"].includes(r.severity) ? r.severity : "info") as SignalSeverity,
      title: r.title,
      detail: r.detail,
      detectedAt: r.detected_at,
      payload: r.payload ?? null,
    }))
    .sort(
      (a, b) =>
        (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9) ||
        b.detectedAt.localeCompare(a.detectedAt),
    );
}

/** Marque un signal comme vu (accuse de reception, sans le resoudre). */
export async function markSignalSeen(id: string): Promise<void> {
  const { error } = await supabase
    .from("juliette_signals")
    .update({ status: "seen", updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

/** Ecarte un signal (l'agent juge qu'il n'y a rien a faire). */
export async function dismissSignal(id: string): Promise<void> {
  const { error } = await supabase
    .from("juliette_signals")
    .update({ status: "dismissed", resolved_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

/** Resout un signal (l'agent a agi dessus, ex : a valide une reco campagne). */
export async function resolveSignal(id: string): Promise<void> {
  const { error } = await supabase
    .from("juliette_signals")
    .update({ status: "resolved", resolved_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw new Error(error.message);
}
